import {
    createNavigationContainerRef,
    getStateFromPath,
    LinkingOptions,
    NavigationState,
    PartialState,
    Route
} from '@react-navigation/native';
import * as Linking from 'expo-linking';
import React, { PropsWithChildren, useCallback, useContext, useMemo } from 'react';

import { useAnalytics } from '~/contexts/analytics';
import { useAuth } from '~/contexts/auth';
import * as ErrorDiagnostics from '~/error';
import { AppNavigatorParamList } from '~/navigator/app-navigator';
import SecureStorage from '~/secure-storage';

import { getLinkingConfig } from './linking-config';

export type LinkingContextType = {
    state?: string;
    options: LinkingOptions<AppNavigatorParamList>;
    getAndClearPendingURL: () => Promise<string | Route<string> | null>;
    setPendingURL: (url: string | Route<string>) => void;
};

type LinkingContextProviderProps = PropsWithChildren<object>;

export const LinkingContext = React.createContext<LinkingContextType | undefined>(undefined);

export const navigationRef = createNavigationContainerRef<AppNavigatorParamList>();

const PENDING_URL_STORAGE_KEY = 'myheltti_pending_url';

/**
 * Will become something like `heltti:///`
 * The scheme is defined app.json:expo.scheme
 */
export const scheme = Linking.createURL('/');

/**
 * Only URLs matching these prefixes will be handled. The prefix will be stripped from the URL before parsing.
 * Read more: https://reactnavigation.org/docs/navigation-container/#linkingprefixes
 */
const prefixes = [
    scheme,
    'http://localhost:50001',
    'https://my.heltti.fi',
    'https://my.dev.heltti.fi',
    'https://my.staging.heltti.fi'
];

/**
 * To test links locally, try `xcrun simctl openurl booted <url>`, e.g.
 * xcrun simctl openurl booted heltti:///chats/Q2hhdE5vZGU6Mg==
 */
function LinkingContextProvider(props: LinkingContextProviderProps) {
    const { state } = useAuth();
    const { track } = useAnalytics();
    const { children } = props;

    const setPendingURL = useCallback(async (url: string | Route<string>) => {
        try {
            const value = typeof url === 'string' ? url : JSON.stringify(url);
            await SecureStorage.setItem(PENDING_URL_STORAGE_KEY, value);
        } catch (error) {
            ErrorDiagnostics.error(error, { message: 'Failed to set pending URL in SecureStorage' });
        }
    }, []);

    const getAndClearPendingURL = useCallback(async (): Promise<string | Route<string> | null> => {
        try {
            const urlString = await SecureStorage.getItem(PENDING_URL_STORAGE_KEY);

            if (!urlString) {
                return null;
            }

            await SecureStorage.deleteItem(PENDING_URL_STORAGE_KEY);

            // Link can be either string or json representation of route string
            try {
                return JSON.parse(urlString);
            } catch {
                return urlString;
            }
        } catch (error) {
            ErrorDiagnostics.error(error, { message: 'Failed to get pending URL from SecureStorage' });
            return null;
        }
    }, []);

    const wrappedGetStateFromPath = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (url: string, config: any) => {
            try {
                /**
                 * Resolve the link path. If the user is not authenticated, the config will not contain the desired
                 * path and link path components will not contain the queried path elements. In this case, queue
                 * the corresponding navigation action so that it can be executed after authentication (and any
                 * onboarding steps)
                 */

                const linkState = getStateFromPath(url, config) as PartialState<NavigationState>;

                const authenticatedLinkingConfig = getLinkingConfig(true);
                const authenticatedLinkState = getStateFromPath(url, authenticatedLinkingConfig);

                // Only capture deep links, if url can be linked after authentication
                // Skip external page routes, since external page linking matches to anything
                if (!linkState && authenticatedLinkState && authenticatedLinkState.routes[0].name !== 'external-page') {
                    ErrorDiagnostics.log(`Capturing deep link url '${url}' pending because of authentication`);

                    setPendingURL(url);
                }

                track({ type: 'navigation', action: 'deep-link', detail1: `url=${url.toString()}` });

                return linkState;
            } catch (error: unknown) {
                ErrorDiagnostics.error(error);
            }
        },
        [track, setPendingURL]
    );

    const value = useMemo(() => {
        return {
            options: {
                prefixes,
                config: getLinkingConfig(state === 'authenticated'),
                getStateFromPath: wrappedGetStateFromPath
            },
            getAndClearPendingURL,
            setPendingURL
        };
    }, [getAndClearPendingURL, state, wrappedGetStateFromPath, setPendingURL]);

    return <LinkingContext.Provider value={value}>{children}</LinkingContext.Provider>;
}

export const LinkingConsumer = LinkingContext.Consumer;
export const LinkingProvider = LinkingContextProvider;

export const useLinking = () => {
    const context = useContext(LinkingContext);
    if (!context) {
        throw Error('Cannot use context until it defined');
    }
    return context;
};
